import { useEffect, useRef } from 'react';
import i18n from 'i18next';

interface UseSEOOptions {
  title: string;
  description?: string;
  keywords?: string;
  path?: string;
  image?: string;
}

const localeMap: Record<string, string> = {
  pt: 'pt_BR',
  en: 'en_US',
};

export function useSEO({ title, description, keywords, path, image }: UseSEOOptions) {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://topgym.com.br';
  const url = path ? `${baseUrl}${path}` : baseUrl;
  const defaultImage = 'https://static.readdy.ai/image/c59ecfd5e54a02b33330323f2bdeb10e/221d8c34c1531330f894539ec6c032ae.jpeg?ogv=l5q6a9';
  const ogImage = image || defaultImage;
  const ogLocale = localeMap[i18n.language] || 'pt_BR';

  const prevTitleRef = useRef(document.title);

  useEffect(() => {
    const previousTitle = prevTitleRef.current;
    document.title = title;

    const setMeta = (name: string, content?: string) => {
      if (!content) return;
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const setProperty = (property: string, content?: string) => {
      if (!content) return;
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    setProperty('og:title', title);
    setProperty('og:description', description);
    setProperty('og:url', url);
    setProperty('og:image', ogImage);
    setProperty('og:image:alt', title);
    setProperty('og:type', 'website');
    setProperty('og:locale', ogLocale);
    setProperty('og:site_name', 'Top Gym Cumbuco - Beach Training');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);
    setMeta('twitter:image:alt', title);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, keywords, url, ogImage, ogLocale]);
}